import { VideoNotificationDto } from '../constants';
import { VIDEO_SYNC_VERSION, buildSyncKey } from './constants';

/**
 * The last fetched notification queue, per identity, shared by every tab of this app.
 *
 * A tab that opens (or reloads) renders from it straight away and calls the API only when it is older than the
 * recall gap - so 100 open tabs cost one request, not one each. The fetching tab writes it; the others also get the
 * result through the cross-tab channel. Not the source of truth: `/active` is, and the snapshot is replaced on every
 * successful fetch.
 */

interface QueueSnapshot {
  v: number;
  at: number;
  videos: VideoNotificationDto[];
}

const keyFor = (scope: string) => buildSyncKey(scope, 'queue');

/** a snapshot this old is not used even as a first paint - the clock or the data is not trustworthy */
const MAX_SNAPSHOT_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export const readQueueSnapshot = (scope: string): { at: number; videos: VideoNotificationDto[] } | null => {
  try {
    const raw = window.localStorage.getItem(keyFor(scope));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as QueueSnapshot;
    if (!parsed || parsed.v !== VIDEO_SYNC_VERSION || !Array.isArray(parsed.videos)) return null;
    const at = Number(parsed.at);
    const age = Date.now() - at;
    if (!Number.isFinite(at) || age < 0 || age > MAX_SNAPSHOT_AGE_MS) return null;
    return { at, videos: parsed.videos };
  } catch {
    // storage unavailable or corrupted - behave as "nothing cached" and fetch
    return null;
  }
};

export const writeQueueSnapshot = (
  scope: string,
  videos: VideoNotificationDto[],
  at: number = Date.now()
): void => {
  try {
    const snapshot: QueueSnapshot = { v: VIDEO_SYNC_VERSION, at, videos };
    window.localStorage.setItem(keyFor(scope), JSON.stringify(snapshot));
  } catch {
    // quota / private mode - the tabs still sync through the channel, they just fetch on open
  }
};

export const clearQueueSnapshot = (scope: string): void => {
  try {
    window.localStorage.removeItem(keyFor(scope));
  } catch {
    // nothing to do
  }
};
